import axios from 'axios';

// Use relative paths for API calls — ensures same-origin on any deployed domain
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: '/api',
  withCredentials: true, // Important: Send cookies with every request
});

// Add the current browser origin to every request so the backend
// can generate correct email links regardless of deployment domain
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    config.headers['X-Frontend-Origin'] = window.location.origin;
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(window.navigator.userAgent || '');
    config.headers['X-Device-Type'] = isMobile ? 'mobile' : 'web';
  }
  return config;
});

// Token refresh state
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Request interceptor - add auth token from localStorage as fallback
api.interceptors.request.use((config) => {
  // For backwards compatibility, still check localStorage
  // The cookie will be sent automatically due to withCredentials: true
  const token = localStorage.getItem('token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401 errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't try to refresh for auth endpoints
      if (originalRequest.url?.includes('/auth/login') || 
          originalRequest.url?.includes('/auth/register') ||
          originalRequest.url?.includes('/auth/refresh')) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Queue requests while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh the token
        const response = await api.post('/auth/refresh');
        
        // If we get a new token in the response, store it for backwards compatibility
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        
        processQueue(null, response.data.token);
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Clear local storage and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Dispatch custom event for auth context to handle
        window.dispatchEvent(new CustomEvent('auth:session-expired', {
          detail: { message: 'Your session has expired. Please sign in again.' }
        }));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  registerCompany: (data) => api.post('/auth/register-company', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  logoutAll: () => api.post('/auth/logout-all'),
  getMe: () => api.get('/auth/me'),
  checkAuth: () => api.get('/auth/check'),
  refreshToken: () => api.post('/auth/refresh'),
  requestMagicLink: (email) => api.post('/auth/magic-link', { email, frontend_origin: window.location.origin }),
  verifyMagicLink: (token) => api.post('/auth/magic-link/verify', { token }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email, frontend_origin: window.location.origin }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  // Session management (user's own sessions)
  getMySessions: () => api.get('/auth/sessions'),
  revokeSession: (sessionId) => api.delete(`/auth/sessions/${sessionId}`),
  // Home address (staff pinpointing)
  updateHomeAddress: (payload) => api.patch('/auth/me/home-address', payload),
};

// Staff locations (for the manager map)
export const staffLocationsAPI = {
  getAll: () => api.get('/v1/staff/locations'),
};

// Modules API
export const modulesAPI = {
  getAll: () => api.get('/modules'),
  getOne: (moduleId) => api.get(`/modules/${moduleId}`),
  generateScenarioBestPractice: (moduleId, idx) =>
    api.post(`/modules/${moduleId}/scenarios/${idx}/best-practice`),
};

// Training Engine API — unified multi-format modules (script + scenarios + quiz + audio)
export const trainingEngineAPI = {
  list: () => api.get('/training-engine/modules'),
  get: (moduleId) => api.get(`/training-engine/modules/${moduleId}`),
  validate: () => api.get('/training-engine/validate'),
  seed: () => api.post('/training-engine/seed'),
  audioUrl: (moduleId) => `${API}/training-engine/modules/${moduleId}/audio`,
  transcriptUrl: (moduleId) => `${API}/training-engine/modules/${moduleId}/audio/transcript`,
  uploadAudio: (moduleId, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/training-engine/modules/${moduleId}/audio`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  deleteAudio: (moduleId) => api.delete(`/training-engine/modules/${moduleId}/audio`),

  // Progress + certificates (granular training_progress pipeline)
  myProgress: () => api.get('/training/progress/me'),
  moduleProgress: (moduleId) => api.get(`/training/${moduleId}/progress`),
  markScriptViewed: (moduleId) => api.post(`/training/${moduleId}/script/viewed`),
  recordAudioHeartbeat: (moduleId, payload) => api.post(`/training/${moduleId}/audio/heartbeat`, payload),
  markAudioUnavailable: (moduleId, payload) => api.post(`/training/${moduleId}/audio/unavailable`, payload),
  markLessonSectionRead: (moduleId, index, payload) =>
    api.post(`/training/${moduleId}/lesson/section/${index}/read`, payload),
  markLessonTileOpened: (moduleId, tileId) =>
    api.post(`/training/${moduleId}/lesson/tile/${tileId}/opened`),
  submitScenario: (moduleId, index, answer) =>
    api.post(`/training/${moduleId}/scenario/${index}`, { answer }),
  submitScenarioReflection: (moduleId, index, text) =>
    api.post(`/training/${moduleId}/scenario/${index}/reflection`, { text }),
  markScenarioUnavailable: (moduleId, index, reason) =>
    api.post(`/training/${moduleId}/scenario/${index}/unavailable`, { reason }),
  submitQuiz: (moduleId, answers) => api.post(`/training/${moduleId}/quiz`, { answers }),
  submitFinalReflection: (moduleId, text) => api.post(`/training/${moduleId}/reflection`, { text }),
  checkCompletion: (moduleId) => api.post(`/training/${moduleId}/check-completion`),
  myCertificates: () => api.get('/training/certificates/me'),
  certificateDownloadUrl: (certificateId) => `${API}/training/certificates/${certificateId}/download`,
  /**
   * Trigger an authenticated PDF download of a training certificate.
   *
   * Why this exists instead of a plain `<a href>`:
   *   The download endpoint requires auth. A bare anchor link relies on
   *   the HTTP-only access cookie travelling with the GET request, which
   *   Safari (iOS especially) blocks in cross-context navigations under
   *   its Intelligent Tracking Prevention policy — carers then see a
   *   `{"detail":"Not authenticated"}` JSON instead of a PDF.
   *   This helper fetches the file via the auth-aware axios instance
   *   (Authorization header + cookies) and triggers a client-side
   *   download from the resulting Blob, which works on every browser.
   */
  downloadCertificate: async (certificateId, filename) => {
    const res = await api.get(
      `/training/certificates/${certificateId}/download`,
      { responseType: 'blob' },
    );
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `${certificateId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so Safari has time to start the download.
    setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    return true;
  },
  adminProgressSummary: () => api.get('/training/admin/progress-summary'),
  adminModuleTeam: (moduleId) => api.get(`/training/admin/${moduleId}/team`),
  adminResetProgress: (moduleId, userId) => api.delete(`/training/admin/${moduleId}/${userId}`),
  adminReissueCertificate: (moduleId, userId) =>
    api.post(`/training/admin/${moduleId}/${userId}/reissue`),
  adminNudgeModule: (moduleId, payload = {}) =>
    api.post(`/training/admin/${moduleId}/nudge`, payload),
  adminListNudges: (moduleId) => api.get(`/training/admin/${moduleId}/nudges`),
  adminStaffModuleEvidence: (userId, moduleId) =>
    api.get(`/manager/training/staff/${userId}/module/${moduleId}/evidence`),
  uploadVoiceReflection: (moduleId, file, { onProgress, scope = 'final_reflection', scenarioIndex = null, durationSeconds = null } = {}) => {
    const form = new FormData();
    form.append('file', file);
    form.append('scope', scope);
    if (scenarioIndex !== null && scenarioIndex !== undefined) {
      form.append('scenario_index', String(scenarioIndex));
    }
    if (durationSeconds !== null && durationSeconds !== undefined) {
      form.append('duration_seconds', String(durationSeconds));
    }
    return api.post(`/training/modules/${moduleId}/voice-note`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },

  // Iter 227 — annual refresher + CPD + public verify
  listExpiring: (withinDays = 30) =>
    api.get('/training/admin/certificates/expiring', { params: { within_days: withinDays } }),
  startRefresher: (certificateId) =>
    api.post(`/training/admin/refresher/${certificateId}`),
  cpdRecord: (userId) => api.get(`/training/carer/${userId}/cpd-record`),
  cpdRecordPdfUrl: (userId) => `${API}/training/carer/${userId}/cpd-record.pdf`,
  /**
   * Authenticated CPD record PDF download. See `downloadCertificate`
   * for the rationale on why a JS-driven blob download is required
   * instead of a plain anchor link.
   */
  downloadCpdRecordPdf: async (userId, filename) => {
    const res = await api.get(
      `/training/carer/${userId}/cpd-record.pdf`,
      { responseType: 'blob' },
    );
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || `cpd-record-${userId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1500);
    return true;
  },
  publicVerifyCertificate: (certificateId) =>
    api.get(`/public/verify-certificate/${certificateId}`),

  // Iter 228 — spaced repetition recall quizzes
  recallDue: () => api.get('/training/recall/due'),
  recallGet: (recallId) => api.get(`/training/recall/${recallId}`),
  recallSubmit: (recallId, answers) =>
    api.post(`/training/recall/${recallId}/submit`, { answers }),
  recallOverview: () => api.get('/training/admin/recall-overview'),

  // Iter 228c — content quality analytics (which quiz questions are
  // failed most often across the tenant)
  contentQualityOverview: () => api.get('/training/admin/content-quality'),
  contentQualityModule: (moduleId) =>
    api.get(`/training/admin/content-quality/${moduleId}`),

  // v3 — Learner reflective voice notes
  uploadVoiceNote: (moduleId, file, onProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/training/modules/${moduleId}/voice-note`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  voiceNoteStreamUrl: (moduleId, responseId) =>
    `${API}/training/modules/${moduleId}/voice-note/${responseId}`,
};

// Manager Training v3 — Drill-downs, nudge pipeline, voice-note metrics
export const managerTrainingAPI = {
  overview: () => api.get('/manager/training/overview'),
  staffList: () => api.get('/manager/training/staff'),
  staffDetail: (uid) => api.get(`/manager/training/staff/${uid}`),
  nudgeMetrics: (days = 30) => api.get(`/manager/training/nudge/metrics?days=${days}`),
  nudgeROIDigest: (week = 'current') =>
    api.get(`/manager/training/nudge-roi-digest?week=${encodeURIComponent(week)}`),
  runWeeklyDigest: ({ force = false } = {}) =>
    api.post(`/nudge/run-weekly-digest${force ? '?force=true' : ''}`),
};

// Unified Nudge v3 — cooldown matrix + attribution
export const nudgeAPI = {
  send: (payload) => api.post('/nudge/send', payload),
  history: (uid) => api.get(`/nudge/history/${uid}`),
  markOpened: (nudgeId) => api.post(`/nudge/open/${nudgeId}`),
  runAutomated: () => api.post('/nudge/run-automated-triggers'),
  testPush: (payload = {}) => api.post('/push/test', payload),
};

// Learning API
export const learningAPI = {
  startModule: (moduleId) => api.post('/learn/start', { module_id: moduleId }),
  submitQuiz: (moduleId, answers) => api.post('/learn/submit-quiz', { module_id: moduleId, answers }),
  getProgress: () => api.get('/progress/me'),
  getDashboardData: () => api.get('/progress/dashboard-data'),
  bookmarkSection: (moduleId, lastSection) => api.post('/learn/bookmark', { module_id: moduleId, last_section: lastSection }),
};

// Payment API
export const paymentAPI = {
  createCheckout: (packageType) => {
    const origin = window.location.origin;
    return api.post('/payments/checkout', { package: packageType, origin_url: origin });
  },
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Certificate API
export const certificateAPI = {
  getMyCertificates: () => api.get('/certificates/my'),
  downloadCertificate: (certificateId) => api.get(`/certificates/download/${certificateId}`, { responseType: 'blob' }),
  downloadProgrammeCertificate: () => api.get('/certificates/download-programme', { responseType: 'blob' }),
  downloadRenewalCertificate: (certificateId) => api.get(`/certificates/download-renewal/${certificateId}`, { responseType: 'blob' }),
  checkProgrammeEligibility: () => api.get('/certificates/check-programme-eligibility'),
};

// Company API
export const companyAPI = {
  getDashboard: () => api.get('/company/dashboard'),
  getStaff: () => api.get('/company/staff'),
  enrollStaff: (data) => api.post('/company/enroll-staff', data),
  getAnalytics: () => api.get('/company/analytics'),
  getDrilldown: (type, value) => api.get(`/company/analytics/drilldown?type=${encodeURIComponent(type)}&value=${encodeURIComponent(value)}`),
  exportReport: () => api.get('/company/export/compliance-report', { responseType: 'blob' }),
  sendReminders: () => api.post('/company/send-reminders'),
  sendReengagement: () => api.post('/company/send-reengagement'),
  sendWellbeingCheckin: () => api.post('/company/send-wellbeing-checkin'),
  sendMonthlyReport: () => api.post('/company/send-monthly-report'),
  sendFeedbackRequest: () => api.post('/company/send-feedback-request'),
  setSupervisor: (userId, isSupervisor) => api.post('/company/set-supervisor', { user_id: userId, is_supervisor: isSupervisor }),
  getRefresherStatus: () => api.get('/company/refresher-status'),
  sendRefresherReminders: () => api.post('/company/send-refresher-reminders'),
  downloadCompanyCertificate: () => api.get('/company/certificates/company', { responseType: 'blob' }),
  downloadStaffCertificate: (userId) => api.get(`/company/certificates/staff/${userId}`, { responseType: 'blob' }),
  downloadMyStaffCertificate: () => api.get('/company/certificates/my-staff-certificate', { responseType: 'blob' }),
  // Session management (company-wide)
  getCompanySessions: () => api.get('/company/sessions'),
  getSessionStats: () => api.get('/company/sessions/stats'),
  revokeCompanySession: (sessionId) => api.delete(`/company/sessions/${sessionId}`),
  getStaffSessions: (userId) => api.get(`/company/staff/${userId}/sessions`),
  revokeAllStaffSessions: (userId) => api.delete(`/company/staff/${userId}/sessions`),
  // Team Onboarding
  addEmployee: (data) => api.post('/company/add-employee', data),
  linkEmployee: (email) => api.post('/company/link-employee', { email, confirm: true }),
  assignModules: (userId, moduleIds) => api.post('/company/assign-modules', { user_id: userId, module_ids: moduleIds }),
  removeEmployee: (userId) => api.post('/company/remove-employee', { user_id: userId }),
  resendInvitation: (userId) => api.post('/company/resend-invitation', { user_id: userId }),
  // Company Logo
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/company/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteLogo: () => api.delete('/company/logo'),
  getLogoUrl: (companyId) => `${API}/company/logo/${companyId}`,
  // Enhanced Dashboard
  getActivityTimeline: () => api.get('/company/dashboard/activity-timeline'),
  getTopPerformers: () => api.get('/company/dashboard/top-performers'),
  getProgressTrend: () => api.get('/company/dashboard/progress-trend'),
  getUtilisationStats: () => api.get('/company/dashboard/utilisation-stats'),
  getCompanyNews: () => api.get('/company/dashboard/news'),
  getComplianceHeatmap: () => api.get('/company/dashboard/compliance-heatmap'),
  getStaffReviews: () => api.get('/company/staff-reviews'),
  getTrainingRecommendations: (userId) => api.get(`/company/staff/${userId}/training-recommendations`),
  getMyTrainingRecommendations: () => api.get('/company/my-training-recommendations'),
  getDashboardLayout: () => api.get('/company/dashboard-layout'),
  saveDashboardLayout: (widgets) => api.put('/company/dashboard-layout', { widgets }),
  resetDashboardLayout: () => api.delete('/company/dashboard-layout'),
};

// Admin API
export const adminAPI = {
  getEmailTemplates: () => api.get('/admin/email-templates'),
  previewEmailTemplate: (templateId) => api.get(`/admin/email-templates/${templateId}/preview`),
  saveTemplateCustomization: (templateId, data) => api.put(`/admin/email-templates/${templateId}`, data),
  resetTemplateCustomization: (templateId) => api.delete(`/admin/email-templates/${templateId}/customization`),
  getTemplateCustomizations: () => api.get('/admin/email-templates/customizations'),
};

// Media API
export const mediaAPI = {
  getModulesWithMediaStatus: () => api.get('/modules'),
  uploadMedia: (moduleId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/modules/${moduleId}/media`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
  },
  deleteMedia: (moduleId) => api.delete(`/modules/${moduleId}/media`),
};

// Contact API
export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// Wellbeing API
export const wellbeingAPI = {
  checkin: (mood, note) => api.post('/wellbeing/checkin', { mood, note }),
  history: () => api.get('/wellbeing/history'),
};

// Gamification API
export const gamificationAPI = {
  getAllBadges: () => api.get('/gamification/badges'),
  getMyBadges: () => api.get('/gamification/my-badges'),
  getMyStreaks: () => api.get('/gamification/my-streaks'),
  getLeaderboard: (limit = 10) => api.get(`/gamification/leaderboard?limit=${limit}`),
  getStats: () => api.get('/gamification/stats'),
  logActivity: () => api.post('/gamification/activity'),
};

// Visit Verification API
export const visitsAPI = {
  // Client Management (Employer)
  getClients: () => api.get('/visits/clients'),
  getClient: (clientId) => api.get(`/visits/clients/${clientId}`),
  createClient: (data) => api.post('/visits/clients', data),
  updateClient: (clientId, data) => api.put(`/visits/clients/${clientId}`, data),
  archiveClient: (clientId) => api.delete(`/visits/clients/${clientId}`),
  
  // QR Code
  getClientQRCode: (clientId) => api.get(`/visits/clients/${clientId}/qr-code`, { responseType: 'blob' }),
  regenerateQRCode: (clientId) => api.post(`/visits/clients/${clientId}/regenerate-qr`),
  getDynamicQRToken: (clientId) => api.get(`/visits/clients/${clientId}/dynamic-qr-token`),
  
  // Staff Check-in/Check-out
  verifyQR: (qrToken) => api.post('/visits/verify-qr', { qr_token: qrToken }),
  checkIn: (data) => api.post('/visits/check-in', data),
  forceCheckout: (data) => api.post('/visits/force-checkout', data),
  checkOut: (visitId, notes) => api.post('/visits/check-out', { visit_id: visitId, notes }),
  completeVisit: (visitSummary) => api.post('/visits/complete-visit', visitSummary),
  getMyActiveVisit: () => api.get('/visits/my-active-visit'),
  
  // Visit Logs (Employer)
  getLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/visits/logs${query ? `?${query}` : ''}`);
  },
  exportLogs: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/visits/logs/export${query ? `?${query}` : ''}`, { responseType: 'blob' });
  },
  getStats: () => api.get('/visits/stats'),
  
  // Scheduled Visits
  getScheduledVisits: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/visits/scheduled${query ? `?${query}` : ''}`);
  },
  createScheduledVisit: (data) => api.post('/visits/scheduled', data),
  updateScheduledVisit: (scheduleId, data) => api.put(`/visits/scheduled/${scheduleId}`, data),
  deleteScheduledVisit: (scheduleId) => api.delete(`/visits/scheduled/${scheduleId}`),
  getTodaySchedule: () => api.get('/visits/today-schedule'),
  
  // Alerts
  getAlerts: (params = {}) => {
    const query = new URLSearchParams(params).toString();
    return api.get(`/visits/alerts${query ? `?${query}` : ''}`);
  },
  getAlertStats: () => api.get('/visits/alerts/stats'),
  acknowledgeAlert: (alertId, resolutionNotes) => api.post('/visits/alerts/acknowledge', { alert_id: alertId, resolution_notes: resolutionNotes }),
  checkScheduledVisits: () => api.post('/visits/alerts/check-scheduled'),
  
  // Settings
  getSettings: () => api.get('/visits/settings'),
  updateSettings: (data) => api.put('/visits/settings', data),
  syncOfflineVisit: (entry) => api.post('/visits/sync', entry),
  getSyncHistory: () => api.get('/visits/sync-history'),
  getCompanyVisitReport: (params) => api.get('/visits/company-report', { params }),
  getStaffVisitHistory: (staffId) => api.get(`/visits/staff/${staffId}/history`),
  getClientVisitHistory: (clientId) => api.get(`/visits/clients/${clientId}/history`),
  getSyncOverview: () => api.get('/visits/sync-overview'),
  getFraudDashboard: (params) => api.get('/visits/fraud-dashboard', { params }),
  generateModuleQR: (moduleId) => api.post(`/visits/modules/${moduleId}/qr-code`, null, { responseType: 'blob' }),
  listModuleQRCodes: () => api.get('/visits/modules/qr-codes'),
  getSafeguardingAlerts: () => api.get('/visits/safeguarding-alerts'),
  acknowledgeSafeguardingAlert: (alertId) => api.put(`/visits/safeguarding-alerts/${alertId}/acknowledge`),
  resolveSafeguardingAlert: (alertId, notes) => api.put(`/visits/safeguarding-alerts/${alertId}/resolve`, null, { params: { resolution_notes: notes } }),

  // Geocoding
  geocodeAddress: (address, postcode) => api.post('/visits/geocode', { address, postcode }),
  geocodeClient: (clientId) => api.post(`/visits/clients/${clientId}/geocode`),

  // Visit Details with Location
  getVisitDetails: (visitId) => api.get(`/visits/visit-details/${visitId}`),
};

// ── Food & Drink Logging API ──
export const foodDrinkAPI = {
  getPresets: () => api.get('/food-drink/presets'),
  getMostCommon: (carerId) => api.get(`/food-drink/most-common/${carerId}`),
  createLog: (data) => api.post('/food-drink/log', data),
  getLog: (visitId) => api.get(`/food-drink/log/${visitId}`),
  batchSync: (logs) => api.post('/food-drink/sync', { logs }),
  getAudit: (visitId) => api.get(`/food-drink/audit/${visitId}`),
  managerGetLogs: (limit) => api.get('/food-drink/manager/logs', { params: { limit } }),
};

// Push Notifications API
export const notificationsAPI = {
  // VAPID key for subscription
  getVapidPublicKey: () => api.get('/notifications/vapid-public-key'),
  
  // Subscription management
  subscribe: (subscription, deviceName) => api.post('/notifications/subscribe', { 
    subscription, 
    device_name: deviceName 
  }),
  unsubscribe: (endpoint) => api.delete(`/notifications/unsubscribe?endpoint=${encodeURIComponent(endpoint)}`),
  getSubscriptions: () => api.get('/notifications/subscriptions'),
  
  // Preferences
  getPreferences: () => api.get('/notifications/preferences'),
  updatePreferences: (prefs) => api.put('/notifications/preferences', prefs),
  
  // Test & Admin
  sendTest: (title, body) => api.post('/notifications/test', { title, body }),
  checkUpcomingVisits: () => api.post('/notifications/check-upcoming-visits'),
  getStats: () => api.get('/notifications/admin/stats'),
  
  // In-App Messages
  getMessages: () => api.get('/notifications/messages'),
  getMessage: (messageId) => api.get(`/notifications/messages/${messageId}`),
  markAllRead: () => api.post('/notifications/messages/mark-read'),
  sendMessage: (data) => api.post('/notifications/messages/send', data),
  
  // Feedback
  getFeedback: () => api.get('/notifications/feedback'),
};

// Care Plans API
export const carePlansAPI = {
  init: (clientId) => api.post(`/care-plans/${clientId}`),
  get: (clientId) => api.get(`/care-plans/${clientId}`),
  getSummary: (clientId) => api.get(`/care-plans/${clientId}/summary`),
  updateModule: (clientId, moduleName, data) => api.put(`/care-plans/${clientId}/modules/${moduleName}`, { data }),
  getVersions: (clientId) => api.get(`/care-plans/${clientId}/versions`),
  getVersion: (clientId, versionId) => api.get(`/care-plans/${clientId}/versions/${versionId}`),
  restoreVersion: (clientId, versionId) => api.post(`/care-plans/${clientId}/versions/${versionId}/restore`),
  exportPdf: (clientId, versionId) => {
    const params = versionId ? `?version_id=${versionId}` : '';
    return api.get(`/care-plans/${clientId}/export-pdf${params}`, { responseType: 'blob' });
  },
  // Approvals
  getPendingApprovals: () => api.get('/care-plans/approvals/pending'),
  getAllApprovals: (status) => api.get(`/care-plans/approvals/all${status ? `?status=${status}` : ''}`),
  getClientPending: (clientId) => api.get(`/care-plans/${clientId}/pending`),
  approveChange: (approvalId) => api.post(`/care-plans/approvals/${approvalId}/approve`),
  rejectChange: (approvalId, reason) => api.post(`/care-plans/approvals/${approvalId}/reject`, { reason }),
  // Audit
  getAuditReport: (clientId, fromDate, toDate) => {
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/care-plans/${clientId}/audit-report${qs}`, { responseType: 'blob' });
  },
};

// News API
export const newsAPI = {
  getLatest: () => api.get('/news'),
};

// Onboarding API
export const onboardingAPI = {
  getChecklist: () => api.get('/onboarding/checklist'),
  dismiss: () => api.post('/onboarding/dismiss'),
  getCompanyConfig: () => api.get('/onboarding/company-config'),
  updateCompanyConfig: (data) => api.put('/onboarding/company-config', data),
  resetCompanyConfig: (jobTitle) => api.delete(`/onboarding/company-config/${encodeURIComponent(jobTitle)}`),
};

// Care Module Onboarding API
export const careModuleAPI = {
  getStatus: () => api.get('/onboarding/care-module/status'),
  activate: (data) => api.post('/onboarding/care-module/activate', data),
  mapCertificates: (certificateIds) => api.post('/onboarding/care-module/map-certificates', { certificate_ids: certificateIds }),
  getReadiness: () => api.get('/onboarding/care-module/readiness'),
};

// Certificates API (V6)
export const certificatesAPI = {
  upload: (formData) => api.post('/v6/certificates/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: () => api.get('/v6/certificates'),
  get: (id) => api.get(`/v6/certificates/${id}`),
};

// Care Certificate API
export const careCertificateAPI = {
  getStandards: () => api.get('/care-certificate/standards'),
  getProgress: () => api.get('/care-certificate/progress'),
  getCompletion: () => api.get('/care-certificate/completion'),
  uploadEvidence: (standardId, formData) => api.post(`/care-certificate/evidence/${standardId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  signoffWorker: (standardId, workerId, data) => api.post(`/care-certificate/signoff/${standardId}/${workerId}`, data),
  getBadge: () => api.get('/care-certificate/badge'),
  getManagerOverview: () => api.get('/care-certificate/manager/overview'),
  // P1: Manager per-worker endpoints
  getWorkerStandards: (workerId) => api.get(`/care-certificate/manager/${workerId}/standards`),
  getWorkerEvidence: (workerId, params) => api.get(`/care-certificate/manager/${workerId}/evidence`, { params }),
  getWorkerRiskFlags: (workerId) => api.get(`/care-certificate/manager/${workerId}/risk-flags`),
  managerSignoff: (workerId, standardId, data) => api.post(`/care-certificate/manager/${workerId}/signoff/${standardId}`, data),
  reviewEvidence: (workerId, evidenceId, formData) => api.post(`/care-certificate/manager/${workerId}/evidence/${evidenceId}/review`, formData),
  // Badge PDF & Verification
  downloadBadgePdf: () => api.get('/care-certificate/badge/pdf', { responseType: 'blob' }),
  verifyBadge: (token) => api.get(`/care-certificate/verify/${token}`),
};

// Incidents API
export const incidentsAPI = {
  getTypes: () => api.get('/incidents/types'),
  getClients: () => api.get('/incidents/clients'),
  create: (data) => api.post('/incidents', data),
  list: (params) => api.get('/incidents', { params }),
  get: (id) => api.get(`/incidents/${id}`),
  addAddendum: (id, data) => api.post(`/incidents/${id}/addendum`, data),
  addComment: (id, data) => api.post(`/incidents/${id}/comment`, data),
  requestClarification: (id, data) => api.put(`/incidents/${id}/request-clarification`, data),
  close: (id, data) => api.put(`/incidents/${id}/close`, data),
  uploadEvidence: (id, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/incidents/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  downloadEvidence: (id, evidenceId) => api.get(`/incidents/${id}/evidence/${evidenceId}/download`, { responseType: 'blob' }),
  deleteEvidence: (id, evidenceId) => api.delete(`/incidents/${id}/evidence/${evidenceId}`),
  exportPdf: (id) => api.get(`/incidents/${id}/export-pdf`, { responseType: 'blob' }),
};


// ── Medications API ──
export const medicationsAPI = {
  getAll: (clientId, activeOnly) => api.get(`/medications/${clientId}`, { params: { active_only: activeOnly || false } }),
  create: (clientId, data) => api.post(`/medications/${clientId}`, data),
  update: (clientId, medId, data) => api.put(`/medications/${clientId}/${medId}`, data),
  archive: (clientId, medId) => api.patch(`/medications/${clientId}/${medId}/archive`),
  bulkImport: (clientId, data) => api.post(`/medications/${clientId}/import`, data),
  logDispensing: (clientId, data) => api.post(`/medications/${clientId}/dispensing`, data),
  getDispensingLog: (clientId, medId) => api.get(`/medications/${clientId}/dispensing`, { params: { medication_id: medId || undefined } }),
  getStock: (clientId) => api.get(`/medications/${clientId}/stock`),
  reconcile: (clientId, data) => api.post(`/medications/${clientId}/reconciliation`, data),
  getReconciliations: (clientId) => api.get(`/medications/${clientId}/reconciliation`),
};

// ── MAR API ──
export const marAPI = {
  getChart: (clientId, date) => api.get(`/mar/${clientId}/chart`, { params: { date } }),
  createRecord: (clientId, data) => api.post(`/mar/${clientId}/record`, data),
  witnessSign: (clientId, recordId, data) => api.post(`/mar/${clientId}/record/${recordId}/witness`, data),
  getRecords: (clientId, params) => api.get(`/mar/${clientId}/records`, { params }),
  getDue: (clientId) => api.get(`/mar/${clientId}/due`),
  syncRecords: (data) => api.post('/mar/sync/records', data),
  getAnalytics: (params) => api.get('/mar/analytics', { params }),
};

// ── Manager Dashboard API ──
export const managerAPI = {
  liveVisits: (params) => api.get('/manager/visits/live', { params }),
  visitDetail: (visitId) => api.get(`/manager/visits/${visitId}`),
  visitHistory: (params) => api.get('/manager/visits/history', { params }),
  alerts: (params) => api.get('/manager/alerts', { params }),
  updateAlert: (alertId, data) => api.patch(`/manager/alerts/${alertId}`, data),
  reviewAlert: (alertId, data) => api.post(`/manager/alerts/${alertId}/review`, data || {}),
  clientProfile: (clientId) => api.get(`/manager/clients/${clientId}`),
  carerPerformance: (carerId) => api.get(`/manager/carers/${carerId}`),
  carerSupportStatus: (carerId) => api.get(`/manager/carers/${carerId}/support-status`),
  analyticsOverview: () => api.get('/manager/analytics/overview'),
  medicationDashboard: () => api.get('/manager/medications/dashboard'),
  environmentalDashboard: (params) => api.get('/manager/environmental/dashboard', { params }),
  predictiveAlerts: () => api.get('/manager/predictive-alerts'),
  notificationCentre: () => api.get('/manager/notification-centre'),
  markNotificationsRead: () => api.post('/manager/notification-centre/mark-read'),
};

// ── Carer Visit API ──
export const carerVisitAPI = {
  todayVisits: () => api.get('/carer/visits/today'),
  startVisit: (visitId) => api.post(`/carer/visits/${visitId}/start`),
  endVisit: (visitId) => api.post(`/carer/visits/${visitId}/end`),
  logEvent: (visitId, data) => api.post(`/carer/visits/${visitId}/events`, data),
  getMedications: (visitId) => api.get(`/carer/visits/${visitId}/medications`),
  administerMed: (visitId, medId, data) => api.post(`/carer/visits/${visitId}/medications/${medId}/administer`, data),
  uploadAttachment: (visitId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/carer/visits/${visitId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// ── Documents API ──
export const documentsAPI = {
  upload: (clientId, file, documentType, source, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('client_id', clientId);
    formData.append('document_type', documentType || 'general');
    formData.append('source', source || 'UPLOAD');
    if (notes) formData.append('notes', notes);
    return api.post('/documents/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadVersion: (documentId, file, source, notes) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('source', source || 'UPLOAD');
    if (notes) formData.append('notes', notes);
    return api.post(`/documents/upload-version/${documentId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getClientDocuments: (clientId, includeArchived) =>
    api.get(`/documents/client/${clientId}`, { params: { include_archived: includeArchived || false } }),
  getDocument: (documentId) => api.get(`/documents/${documentId}`),
  downloadDocument: (documentId) => api.get(`/documents/${documentId}/download`, { responseType: 'blob' }),
  getVersions: (documentId) => api.get(`/documents/${documentId}/versions`),
  archiveDocument: (documentId) => api.delete(`/documents/${documentId}`),
};

// ── Digital eMAR API ──
export const emarAPI = {
  barcodeLookup: (barcodeData, barcodeFormat, clientId) =>
    api.post('/emar/barcode-lookup', { barcode_data: barcodeData, barcode_format: barcodeFormat, client_id: clientId }),
  aiCapture: (imageFile, clientId) => {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('client_id', clientId);
    return api.post('/emar/ai-capture', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  manualEntry: (data) => api.post('/emar/manual-entry', data),
  administer: (data) => api.post('/emar/administer', data),
  safetyCheck: (medicationId) => api.get(`/emar/safety-check/${medicationId}`),
  getHistory: (clientId, days, medicationId, status) =>
    api.get(`/emar/history/${clientId}`, { params: { days, medication_id: medicationId, status } }),
  exportAudit: (clientId, days) =>
    api.get(`/emar/audit-export/${clientId}`, { params: { days } }),
  exportAuditPdf: (clientId, days) =>
    api.get(`/emar/audit-pdf/${clientId}`, { params: { days }, responseType: 'blob' }),
  getVisitCarers: (clientId) =>
    api.get(`/emar/visit-carers/${clientId}`),
  getMedicationStatus: (clientId, scheduledTime) =>
    api.get(`/emar/medication-status/${clientId}`, { params: { scheduled_time: scheduledTime || '' } }),
};

// ── Care Plans v2 API ──
export const carePlansV2API = {
  get: (clientId) => api.get(`/care-plans-v2/${clientId}`),
  save: (clientId, data) => api.post(`/care-plans-v2/${clientId}`, data),
  getVersions: (clientId) => api.get(`/care-plans-v2/${clientId}/versions`),
  getVersion: (clientId, version) => api.get(`/care-plans-v2/${clientId}/versions/${version}`),
  deletePlan: (clientId) => api.delete(`/care-plans-v2/${clientId}`),
  getVisitPreview: (clientId) => api.get(`/care-plans-v2/${clientId}/visit-preview`),
  getAssignments: (clientId) => api.get(`/care-plans-v2/${clientId}/assignments`),
  createAssignment: (clientId, data) => api.post(`/care-plans-v2/${clientId}/assignments`, data),
  updateAssignment: (clientId, assignmentId, data) => api.put(`/care-plans-v2/${clientId}/assignments/${assignmentId}`, data),
  deleteAssignment: (clientId, assignmentId) => api.delete(`/care-plans-v2/${clientId}/assignments/${assignmentId}`),
};

// ── Competency & Rota Eligibility API (V1) ──
export const competencyAPI = {
  getCarerModules: (carerId) => api.get(`/v1/competency/carers/${carerId}/modules`),
  completeInternal: (carerId, moduleId) => api.post(`/v1/competency/carers/${carerId}/modules/${moduleId}/complete-internal`),
  verifyExternal: (carerId, moduleId, formData) => api.post(`/v1/competency/carers/${carerId}/modules/${moduleId}/verify-external`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  legacyTemporary: (carerId, moduleId, formData) => api.post(`/v1/competency/carers/${carerId}/modules/${moduleId}/legacy-temporary`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getRotaEligibility: (carerId) => api.get(`/v1/competency/carers/${carerId}/rota-eligibility`),
  getBlockedCarers: () => api.get('/v1/competency/blocked-carers'),
  getAuditLog: (params) => api.get('/v1/competency/audit-log', { params }),
  bulkLegacyTemporary: (data) => api.post('/v1/competency/bulk-legacy-temporary', data),
};

// ── Staff Directory API (V1) ──
export const staffAPI = {
  getDirectory: (params) => api.get('/v1/staff/directory', { params }),
  updateStatus: (userId, data) => api.patch(`/v1/staff/${userId}/status`, data),
  updateSkills: (userId, data) => api.put(`/v1/staff/${userId}/skills`, data),
  getSkillTags: () => api.get('/v1/staff/skill-tags'),
  resendInvite: (data) => api.post('/v1/staff/resend-invite', data),
};

// ── Shadowing Sessions API (V1) ──
export const shadowingAPI = {
  createSession: (data) => api.post('/v1/shadowing/sessions', data),
  listSessions: (params) => api.get('/v1/shadowing/sessions', { params }),
  getSession: (sessionId) => api.get(`/v1/shadowing/sessions/${sessionId}`),
  confirmSession: (sessionId, data) => api.post(`/v1/shadowing/sessions/${sessionId}/confirm`, data),
  carerHistory: (carerId, params) => api.get(`/v1/shadowing/carer/${carerId}/history`, { params }),
  getProgress: () => api.get('/v1/shadowing/progress'),
  getSessionTypes: () => api.get('/v1/shadowing/session-types'),
  getAuditLog: (params) => api.get('/v1/shadowing/audit-log', { params }),
};

// ── CQC Compliance API (V1) ──
export const complianceAPI = {
  dashboard: () => api.get('/v1/compliance/dashboard'),
  visits: (params) => api.get('/v1/compliance/visits', { params }),
  medications: (params) => api.get('/v1/compliance/medications', { params }),
  training: (params) => api.get('/v1/compliance/training', { params }),
  carePlanReviews: (params) => api.get('/v1/compliance/care-plan-reviews', { params }),
  incidents: (params) => api.get('/v1/compliance/incidents', { params }),
  auditTrail: (params) => api.get('/v1/compliance/audit-trail', { params }),
  cqcExport: (params) => api.get('/v1/compliance/cqc-export', { params }),
};

// ── Billing / Subscription API (V1) ──
export const billingAPI = {
  createCheckoutSession: (tier) => {
    const origin = window.location.origin;
    return api.post('/v1/billing/create-checkout-session', { tier, origin_url: origin });
  },
  getSubscription: () => api.get('/v1/billing/subscription'),
  createCustomerPortal: () => {
    const origin = window.location.origin;
    return api.post('/v1/billing/customer-portal', { origin_url: origin });
  },
  getCheckoutStatus: (sessionId) => api.get(`/v1/billing/checkout-status/${sessionId}`),
  trackTrialFeature: (featureKey) => api.post('/v1/billing/trial-usage/track', { feature_key: featureKey }),
  getTrialUsageSummary: () => api.get('/v1/billing/trial-usage/summary'),
  getTrialAnalytics: () => api.get('/v1/billing/trial-analytics'),
  getFFEStatus: () => api.get('/v1/billing/ffe-status'),
  getFFEAudit: () => api.get('/v1/billing/ffe-audit'),
};

// ── Integration Hub API ──
export const integrationAPI = {
  // CM2000
  storeCM2000Credentials: (data) => api.post('/integration-hub/cm2000/credentials', data),
  testCM2000Connection: () => api.post('/integration-hub/cm2000/test-connection'),
  getCM2000Status: () => api.get('/integration-hub/cm2000/status'),
  removeCM2000Credentials: () => api.delete('/integration-hub/cm2000/credentials'),
  pullRota: () => api.post('/integration-hub/cm2000/pull/rota'),
  pullVisits: () => api.post('/integration-hub/cm2000/pull/visits'),
  pullCalls: () => api.post('/integration-hub/cm2000/pull/calls'),
  pullMileage: () => api.post('/integration-hub/cm2000/pull/mileage'),
  pushStaff: (data) => api.post('/integration-hub/cm2000/push/staff', data),
  pushTraining: (data) => api.post('/integration-hub/cm2000/push/training', data),
  // Payroll
  savePayRules: (data) => api.post('/integration-hub/payroll/pay-rules', data),
  getPayRules: () => api.get('/integration-hub/payroll/pay-rules'),
  generatePayroll: (data) => api.post('/integration-hub/payroll/generate', data),
  exportCSV: (data, target) => api.post(`/integration-hub/payroll/export/csv?target_system=${target}`, data, { responseType: 'blob' }),
  exportJSON: (data, target) => api.post(`/integration-hub/payroll/export/json?target_system=${target}`, data),
  saveExportSchedule: (data) => api.post('/integration-hub/payroll/schedules', data),
  getExportSchedules: () => api.get('/integration-hub/payroll/schedules'),
  getPayrollExports: () => api.get('/integration-hub/payroll/exports'),
  // Hub Core
  getConnectors: () => api.get('/integration-hub/connectors'),
  getConnectorHealth: (system) => api.get(`/integration-hub/connectors/${system}/health`),
  getSchedulerStatus: () => api.get('/integration-hub/scheduler/status'),
  getAuditLog: (params) => api.get('/integration-hub/audit-log', { params }),
  getRetryQueue: () => api.get('/integration-hub/retry-queue'),
  getDLQ: () => api.get('/integration-hub/dlq'),
  getAlerts: () => api.get('/integration-hub/alerts'),
  acknowledgeAlert: (index) => api.post(`/integration-hub/alerts/${index}/acknowledge`),
  // Data
  getShiftRecords: () => api.get('/integration-hub/data/shifts'),
  getVisitRecords: () => api.get('/integration-hub/data/visits'),
  getCallRecords: () => api.get('/integration-hub/data/calls'),
  getMileageRecords: () => api.get('/integration-hub/data/mileage'),
};

// ── Competency Templates V2 API ──
export const templatesAPI = {
  list: (params) => api.get('/v2/templates', { params }),
  get: (id) => api.get(`/v2/templates/${id}`),
  create: (data) => api.post('/v2/templates', data),
  update: (id, data) => api.put(`/v2/templates/${id}`, data),
  remove: (id) => api.delete(`/v2/templates/${id}`),
  createVersion: (id) => api.post(`/v2/templates/${id}/version`),
  getAudit: (id) => api.get(`/v2/templates/${id}/audit`),
};

// ── Competency Sessions V2 API ──
export const sessionsAPI = {
  list: (params) => api.get('/v2/sessions', { params }),
  get: (id) => api.get(`/v2/sessions/${id}`),
  create: (data) => api.post('/v2/sessions', data),
  update: (id, data) => api.put(`/v2/sessions/${id}`, data),
  getStats: () => api.get('/v2/sessions/stats'),
  getAudit: (id) => api.get(`/v2/sessions/${id}/audit`),
  getAuditLog: (id) => api.get(`/v2/sessions/${id}/audit-log`),
  getExplainability: (id) => api.get(`/v2/sessions/${id}/explainability`),
  getReviewDashboard: () => api.get('/v2/sessions/review-dashboard'),
  getCarerSummary: (carerId) => api.get('/v2/sessions/carer-summary', { params: { carer_id: carerId } }),
  submitEvidence: (sessionId, data) => api.post(`/v2/sessions/${sessionId}/evidence`, data),
  listEvidence: (sessionId, params) => api.get(`/v2/sessions/${sessionId}/evidence`, { params }),
  reviewCompetency: (sessionId, competencyId, data) => api.post(`/v2/sessions/${sessionId}/competencies/${competencyId}/review`, data),
  signOff: (sessionId, data) => api.post(`/v2/sessions/${sessionId}/sign-off`, data),
};

export const notificationsV2API = {
  list: (params) => api.get('/v2/notifications', { params }),
  unreadCount: () => api.get('/v2/notifications/unread-count'),
  markRead: (data) => api.post('/v2/notifications/mark-read', data),
};

export const autoAssignAPI = {
  rank: (data) => api.post('/v3/auto-assign', data),
  execute: (data) => api.post('/v3/auto-assign/execute', data),
  eligibility: (carerId, templateId) => api.get(`/v3/auto-assign/eligibility/${carerId}`, { params: { template_id: templateId } }),
  multiBranch: (data) => api.post('/v3/auto-assign/multi-branch', data),
  getWeightConfig: () => api.get('/v3/auto-assign/weight-config'),
  saveWeightConfig: (data) => api.put('/v3/auto-assign/weight-config', data),
  resetWeightConfig: () => api.delete('/v3/auto-assign/weight-config'),
};

export const branchesAPI = {
  list: () => api.get('/v3/branches'),
  create: (data) => api.post('/v3/branches', data),
  update: (id, data) => api.put(`/v3/branches/${id}`, data),
  assignStaff: (id, userIds) => api.post(`/v3/branches/${id}/assign-staff`, { user_ids: userIds }),
  overview: () => api.get('/v3/branches/overview'),
  competencyStats: (id) => api.get(`/v3/branches/${id}/competency-stats`),
  staffingGaps: (id) => api.get(`/v3/branches/${id}/staffing-gaps`),
};

export const intelligenceAPI = {
  readinessIndex: (branchId) => api.get('/v3/intelligence/readiness-index', { params: branchId ? { branchId } : {} }),
  riskHeatmap: (branchId) => api.get('/v3/intelligence/risk-heatmap', { params: branchId ? { branchId } : {} }),
  supervisorWorkload: (branchId) => api.get('/v3/intelligence/supervisor-workload', { params: branchId ? { branchId } : {} }),
  expiryForecast: (branchId, window) => api.get('/v3/intelligence/expiry-forecast', { params: { ...(branchId ? { branchId } : {}), ...(window ? { window } : {}) } }),
};

export const pdfExportAPI = {
  branchReadiness: (params) => api.get('/v1/pdf/branch-readiness', { params, responseType: 'blob' }),
  competencyProfile: (params) => api.get('/v1/pdf/competency-profile', { params, responseType: 'blob' }),
  expiryForecast: (params) => api.get('/v1/pdf/expiry-forecast', { params, responseType: 'blob' }),
  supervisorWorkload: (params) => api.get('/v1/pdf/supervisor-workload', { params, responseType: 'blob' }),
  analyticsOverview: () => api.get('/v1/pdf/analytics-overview', { responseType: 'blob' }),
  analyticsCsv: () => api.get('/v1/pdf/analytics-csv', { responseType: 'blob' }),
};

export const reportSchedulesAPI = {
  list: () => api.get('/v1/report-schedules'),
  create: (data) => api.post('/v1/report-schedules', data),
  update: (id, data) => api.put(`/v1/report-schedules/${id}`, data),
  remove: (id) => api.delete(`/v1/report-schedules/${id}`),
  runNow: (id) => api.post(`/v1/report-schedules/${id}/run-now`),
};

// ── Schedule API ──
export const scheduleAPI = {
  getWeekly: (weekStart) => api.get('/schedule/weekly', { params: { week_start: weekStart } }),
  getCarerRota: (carerId, weekStart) => api.get(`/schedule/carer-rota/${carerId}`, { params: { week_start: weekStart } }),
  assignCarer: (slotId, data) => api.post(`/schedule/slots/${slotId}/assign`, data),
  unassignCarer: (slotId, data) => api.post(`/schedule/slots/${slotId}/unassign`, data),
  cancelSlot: (slotId, data) => api.put(`/schedule/slots/${slotId}/cancel`, data),
  restoreSlot: (slotId) => api.put(`/schedule/slots/${slotId}/restore`),
  getVisitPackage: (slotId) => api.get(`/schedule/slots/${slotId}/visit-package`),
  getAvailableCarers: () => api.get('/schedule/available-carers'),
  generateSlots: (weekStart) => api.post('/schedule/generate', null, { params: { week_start: weekStart } }),
  getSuggestions: (slotId) => api.get(`/schedule/slots/${slotId}/suggestions`),
  autoAssignWeek: (weekStart) => api.post('/schedule/auto-assign-week', null, { params: { week_start: weekStart } }),
  autopilot: (weekStart, clientId) => api.post('/schedule/autopilot', null, {
    params: { week_start: weekStart, client_id: clientId },
  }),
  nearestCarers: (clientId, limit = 5) =>
    api.get('/schedule/nearest-carers', { params: { client_id: clientId, limit } }),
  getRotaReview: (weekStart) => api.get('/schedule/rota-review', { params: { week_start: weekStart } }),
};


// ── Capability Passport API ──
export const passportAPI = {
  generate: (staffId) => api.post(`/v5/passport/${staffId}/generate`),
  get: (staffId, mode) => api.get(`/v5/passport/${staffId}`, { params: mode ? { mode } : {} }),
  verify: (token) => api.get(`/v5/passport/verify/${token}`),
  exportPDF: (staffId, regulator = false) => api.get(`/v5/passport/${staffId}/export/pdf`, { params: { regulator }, responseType: 'blob' }),
  // Self-service (my passport)
  generateMy: () => api.post('/v5/passport/my/generate'),
  getMy: () => api.get('/v5/passport/my'),
  getMyVerificationHistory: () => api.get('/v5/passport/my/verification-history'),
  // Analytics (5.6)
  analyticsOverview: (from, to) => api.get('/v5/analytics/passport/overview', { params: { from, to } }),
  analyticsBranches: (from, to) => api.get('/v5/analytics/passport/branches', { params: { from, to } }),
  analyticsStaff: (sort, limit, from, to) => api.get('/v5/analytics/passport/staff', { params: { sort, limit, from, to } }),
  analyticsTimeseries: (groupBy, branchId, from, to) => api.get('/v5/analytics/passport/timeseries', { params: { group_by: groupBy, branch_id: branchId, from, to } }),
  analyticsAnomalies: (from, to) => api.get('/v5/analytics/passport/anomalies', { params: { from, to } }),
  // Transfer (5.4)
  transferExport: (staffId, destinationEmail) => api.post(`/v5/passport/transfer/export/${staffId}`, destinationEmail ? { destination_email: destinationEmail } : {}),
  transferValidate: (bundle) => api.post('/v5/passport/transfer/validate', { bundle }),
  transferImport: (data) => api.post('/v5/passport/transfer/import', data),
  transferReject: (transferId, reason) => api.post(`/v5/passport/transfer/reject/${transferId}`, null, { params: reason ? { reason } : {} }),
  transferHistory: (params) => api.get('/v5/passport/transfer/history', { params }),
  transferDetail: (transferId) => api.get(`/v5/passport/transfer/${transferId}`),
  // Offline sync
  syncOfflineScan: (scan) => api.post('/v5/passport/offline-scan-sync', scan),
};

// ═══ Registry Connector API (Phase 5.5) ═══
export const registryAPI = {
  getTypes: () => api.get('/v5/registry/types'),
  listConnectors: () => api.get('/v5/registry/connectors'),
  createConnector: (data) => api.post('/v5/registry/connectors', data),
  updateConnector: (id, data) => api.put(`/v5/registry/connectors/${id}`, data),
  deleteConnector: (id) => api.delete(`/v5/registry/connectors/${id}`),
  testConnector: (id) => api.post(`/v5/registry/connectors/${id}/test`),
  syncRegistry: (id) => api.post(`/v5/registry/sync/${id}`),
  getSyncHistory: (params) => api.get('/v5/registry/sync/history', { params }),
  getStaffCompliance: (staffId) => api.get(`/v5/registry/compliance/${staffId}`),
  lookupPractitioner: (data) => api.post('/v5/registry/lookup', data),
};

// ═══ External Certificate API (Phase 6.1 + 6.2) ═══
export const externalCertificateAPI = {
  upload: (formData) => api.post('/v6/certificates/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  list: (params) => api.get('/v6/certificates', { params }),
  get: (id) => api.get(`/v6/certificates/${id}`),
  update: (id, data) => api.put(`/v6/certificates/${id}`, data),
  delete: (id) => api.delete(`/v6/certificates/${id}`),
  download: (id) => api.get(`/v6/certificates/file/${id}`, { responseType: 'blob' }),
  queue: (status) => api.get('/v6/certificates/queue', { params: { status: status || 'pending' } }),
  verify: (id, notes) => api.post(`/v6/certificates/${id}/verify`, { notes }),
  reject: (id, reason) => api.post(`/v6/certificates/${id}/reject`, { reason }),
};

// ═══ Passport Request API (Phase 6.4) ═══
export const passportRequestAPI = {
  create: (data) => api.post('/v6/passport-request', data),
  incoming: (params) => api.get('/v6/passport-request/incoming', { params }),
  approve: (id, note) => api.post(`/v6/passport-request/${id}/approve`, { note }),
  reject: (id, note) => api.post(`/v6/passport-request/${id}/reject`, { note }),
  viewPublic: (token) => api.get(`/v6/passport-request/view/${token}`),
};

// ═══ Passport Billing API (Phase 6.5) ═══
export const passportBillingAPI = {
  getTiers: () => api.get('/v6/passport-billing/tiers'),
  getMyTier: () => api.get('/v6/passport-billing/my-tier'),
  carerCheckout: (data) => api.post('/v6/passport-billing/carer-checkout', data),
  employerCheckout: (data) => api.post('/v6/passport-billing/employer-checkout', data),
  getEmployerCredits: (email) => api.get(`/v6/passport-billing/employer-credits/${encodeURIComponent(email)}`),
  // Worker tier checkout (Universal underpins CareSkillset)
  workerCheckout: (plan) => {
    const origin = window.location.origin;
    return api.post('/v6/passport-billing/worker-checkout', { plan, origin_url: origin });
  },
  getWorkerTier: () => api.get('/v6/passport-billing/worker-tier'),
};

// Worker plan IDs — single source of truth for frontend
export const WORKER_PLANS = {
  universal_passport: 'universal_passport',
  universal_plus_care_module: 'universal_plus_care_module',
};

// ═══ Universal Passport API (Phase 7 — Standalone) ═══
export const universalPassportAPI = {
  register: (data) => api.post('/v7/passport/register', data),
  checkout: (data) => api.post('/v7/passport/checkout', data),
  paymentStatus: () => api.get('/v7/passport/payment-status'),
  identityStart: (data) => api.post('/v7/passport/identity/start', data || {}),
  identityStatus: () => api.get('/v7/passport/identity/status'),
  uploadDocument: (formData) => api.post('/v7/passport/documents/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  listDocuments: (params) => api.get('/v7/passport/documents', { params }),
  getCategories: () => api.get('/v7/passport/categories'),
  getProfile: () => api.get('/v7/passport/profile'),
  // Sharing (Phase 2.5)
  createShare: (data) => api.post('/v7/passport/share/create', data),
  revokeShare: (data) => api.post('/v7/passport/share/revoke', data),
  listShares: () => api.get('/v7/passport/shares'),
  getPublicShare: (token, mode) => api.get(`/v7/passport/share/${token}`, { params: mode ? { mode } : {} }),
  // Geo-anomaly & Analytics (Phase 2.5+)
  getShareAnomalies: () => api.get('/v7/passport/share/anomalies'),
  getAccessAnalytics: () => api.get('/v7/passport/share/access-analytics'),
  // Passport Object (Phase 2)
  issuePassport: (data) => api.post('/v7/passport/issue', data || {}),
  getPassportObject: () => api.get('/v7/passport/object'),
  verifyPassport: (passportId) => api.get(`/v7/passport/verify/${passportId}`),
  getPremiumAddons: () => api.get('/v7/passport/premium/addons'),
  getAnalyticsSchema: () => api.get('/v7/passport/analytics/schema'),
  // Company Linking (Phase 3)
  searchCompanies: (q) => api.get(`/v7/passport/linking/companies/search?q=${encodeURIComponent(q)}`),
  submitLinkRequest: (data) => api.post('/v7/passport/linking/request', data),
  listLinkRequests: () => api.get('/v7/passport/linking/requests'),
  approveLinkRequest: (data) => api.post('/v7/passport/linking/approve', data),
  rejectLinkRequest: (data) => api.post('/v7/passport/linking/reject', data),
  getLinkingStatus: () => api.get('/v7/passport/linking/status'),
  // Premium (Phase 3)
  getPremiumStatus: () => api.get('/v7/passport/premium/status'),
  subscribePremium: (data) => api.post('/v7/passport/premium/subscribe', data),
  cancelPremium: (data) => api.post('/v7/passport/premium/cancel', data),
  // Employer (Phase 3)
  generateFingerprint: () => api.post('/v7/passport/employer/fingerprint'),
  getEmployerBadge: (companyId) => api.get(`/v7/passport/employer/${companyId}/badge`),
};

// ── Hybrid Verification (DBS + RTW) ──
export const verificationAPI = {
  getResolved: (workerId) => api.get(`/verification/${workerId}/resolved`),
  getAuditTrail: (workerId, domain) => api.get(`/verification/${workerId}/audit-trail`, { params: domain ? { domain } : {} }),
  getHistory: (workerId, domain) => api.get(`/verification/${workerId}/history`, { params: domain ? { domain } : {} }),
  employerVerifyDbs: (data) => api.post('/verification/dbs/employer-verify', data),
  employerVerifyRtw: (data) => api.post('/verification/rtw/employer-verify', data),
  workerUploadDbs: (formData) => api.post('/verification/dbs/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  workerUploadRtw: (formData) => api.post('/verification/rtw/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  linkExternalDbs: (data) => api.post('/verification/dbs/link-external', data),
  linkExternalRtw: (data) => api.post('/verification/rtw/link-external', data),
  revokeRecord: (domain, recordId, data) => api.post(`/verification/${domain}/${recordId}/revoke`, data),
  getSyncStatus: (workerId) => api.get(`/verification/${workerId}/sync-status`),
  refreshExternal: (domain, data) => api.post(`/verification/${domain}/refresh`, data),
};

// ── Employer Verification Dashboard ──
export const verificationDashboardAPI = {
  getSummary: () => api.get('/company/verification-summary'),
  getWorkersStatus: (params) => api.get('/company/workers/verification-status', { params }),
  bulkVerify: (data) => api.post('/company/verify/bulk', data),
  exportCSV: (params) => api.get('/company/verification-export', { params, responseType: 'blob' }),
  getExpiringWorkers: () => api.get('/company/expiring-workers'),
};



// ── Client Invoicing (P0 Private-client billing) ──
export const dispatchAPI = {
  dispatchCarer: (data) => api.post('/schedule/dispatch-carer', data),
  recentDispatches: (params = {}) => api.get('/schedule/dispatches', { params }),
  get: (dispatchId) => api.get(`/schedule/dispatches/${dispatchId}`),
  acknowledge: (dispatchId, data = {}) => api.post(`/schedule/dispatches/${dispatchId}/acknowledge`, data),
  locationPing: (dispatchId, data) => api.post(`/schedule/dispatches/${dispatchId}/location-ping`, data),
  stopSharing: (dispatchId) => api.post(`/schedule/dispatches/${dispatchId}/stop-sharing`),
  active: () => api.get('/schedule/dispatches-active'),
  recall: (dispatchId, data = {}) => api.post(`/schedule/dispatches/${dispatchId}/recall`, data),
  reassign: (dispatchId, data = {}) => api.post(`/schedule/dispatches/${dispatchId}/reassign`, data),
};

export const founderAPI = {
  status: () => api.get('/founder/status'),
  mfaBegin: () => api.post('/founder/mfa/enroll/begin'),
  mfaVerify: (code) => api.post('/founder/mfa/enroll/verify', { code }),
  impersonate: (targetUserId, reason) =>
    api.post('/founder/impersonate', { target_user_id: targetUserId, reason: reason || null }),
  stopImpersonate: () => api.post('/founder/impersonate/stop'),
  companies: () => api.get('/founder/companies'),
  users: (params = {}) => api.get('/founder/users', { params }),
  listFlags: () => api.get('/founder/feature-flags'),
  createFlag: (data) => api.post('/founder/feature-flags', data),
  updateFlag: (flagId, data) => api.patch(`/founder/feature-flags/${flagId}`, data),
  deleteFlag: (flagId) => api.delete(`/founder/feature-flags/${flagId}`),
  auditLog: (limit = 100) => api.get('/founder/audit-log', { params: { limit } }),
  listMaintenanceWindows: () => api.get('/founder/maintenance-windows'),
  createMaintenanceWindow: (data) => api.post('/founder/maintenance-windows', data),
  cancelMaintenanceWindow: (windowId, opts = {}) =>
    api.delete(`/founder/maintenance-windows/${windowId}`, {
      params: opts.cancel_series ? { cancel_series: true } : {},
    }),
};

export const manageAPI = {
  carerRoster: ({ week_start } = {}) =>
    api.get('/manage/carer-roster', { params: week_start ? { week_start } : {} }),
  clientRoster: ({ week_start } = {}) =>
    api.get('/manage/client-roster', { params: week_start ? { week_start } : {} }),
  weeklyOverview: ({ week_start, view = 'carer' } = {}) =>
    api.get('/manage/weekly-overview', {
      params: { view, ...(week_start ? { week_start } : {}) },
    }),
  // Phase 2
  monitoringExceptions: ({ window = '48h', types, status = 'open' } = {}) =>
    api.get('/manage/monitoring/exceptions', {
      params: { window, status, ...(types ? { types } : {}) },
    }),
  monitoringSummary: () => api.get('/manage/monitoring/summary'),
  acknowledgeException: (id, resolution_notes = '') =>
    api.post(`/manage/monitoring/exceptions/${id}/acknowledge`, { resolution_notes }),
  diary: ({ entity_type, entity_id, kinds, date_from, date_to, limit } = {}) =>
    api.get('/manage/diary', {
      params: {
        entity_type, entity_id,
        ...(kinds ? { kinds } : {}),
        ...(date_from ? { date_from } : {}),
        ...(date_to ? { date_to } : {}),
        ...(limit ? { limit } : {}),
      },
    }),
  // Manual assignment helpers
  eligibleCarers: (slot_id) => api.get(`/manage/slots/${slot_id}/eligible-carers`),
  unassignedSlotsForClient: (client_id) =>
    api.get(`/manage/clients/${client_id}/unassigned-slots`),
  assignCarer: (slot_id, carer_id, role = 'primary') =>
    api.post(`/schedule/slots/${slot_id}/assign`, { carer_id, role }),
  unassignCarer: (slot_id, carer_id) =>
    api.post(`/schedule/slots/${slot_id}/unassign`, { carer_id }),
  bulkAssign: (slot_ids, carer_id, role = 'primary') =>
    api.post('/manage/slots/bulk-assign', { slot_ids, carer_id, role }),
  bulkUnassign: (slot_ids, carer_id) =>
    api.post('/manage/slots/bulk-unassign', { slot_ids, carer_id }),
  bulkAutoPreview: (slot_ids) =>
    api.post('/manage/slots/bulk-auto-assign/preview', { slot_ids }),
  bulkAutoCommit: (assignments, role = 'primary') =>
    api.post('/manage/slots/bulk-auto-assign/commit', { assignments, role }),
  // Rota templates
  listTemplates: () => api.get('/manage/rota-templates'),
  createTemplate: (name, pairings, match_quality = 0, description = null) =>
    api.post('/manage/rota-templates', { name, pairings, match_quality, description }),
  deleteTemplate: (template_id) =>
    api.delete(`/manage/rota-templates/${template_id}`),
  applyTemplate: (template_id, slot_ids) =>
    api.post(`/manage/rota-templates/${template_id}/apply`, { slot_ids }),
};

export const dataImportAPI = {
  staffPreview: (file, preset = 'auto') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preset', preset);
    return api.post('/data-import/staff/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  staffCommit: (rows, send_invitations = true) =>
    api.post('/data-import/staff/commit', { rows, send_invitations }),
  clientPreview: (file, preset = 'auto') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preset', preset);
    return api.post('/data-import/clients/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clientCommit: (rows, geocode = true) =>
    api.post('/data-import/clients/commit', { rows, geocode }),
  shiftPreview: (file, preset = 'auto') => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('preset', preset);
    return api.post('/data-import/shifts/preview', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  shiftCommit: (rows, week_start = null, auto_assign = true) =>
    api.post('/data-import/shifts/commit', { rows, week_start, auto_assign }),
};

export const careTasksAPI = {
  catalog: () => api.get('/care-tasks/catalog'),
  createCustom: (name, category, description = '', icon_key = 'ClipboardList', skills_required = []) =>
    api.post('/care-tasks/catalog', { name, category, description, icon_key, skills_required }),
  updateCustom: (task_key, patch) =>
    api.patch(`/care-tasks/catalog/${task_key}`, patch),
  deleteCustom: (task_key) =>
    api.delete(`/care-tasks/catalog/${task_key}`),
  suggestForClient: (client_id, visit_label = null, exclude_task_keys = []) =>
    api.post('/care-tasks/suggest-for-client', { client_id, visit_label, exclude_task_keys }),
};

export const platformAPI = {
  status: () => api.get('/platform/status'),
};

export const managerDashboardAPI = {
  summary: () => api.get('/manager/dashboard-summary'),
};

export const invoicesAPI = {
  list: (params = {}) => api.get('/invoices', { params }),
  get: (invoiceId) => api.get(`/invoices/${invoiceId}`),
  preview: (clientId, periodFrom, periodTo) =>
    api.get('/invoices/billable/preview', {
      params: { client_id: clientId, period_from: periodFrom, period_to: periodTo },
    }),
  generate: (data) => api.post('/invoices/generate', data),
  update: (invoiceId, data) => api.patch(`/invoices/${invoiceId}`, data),
  markPaid: (invoiceId, data) => api.post(`/invoices/${invoiceId}/mark-paid`, data),
  email: (invoiceId, data) => api.post(`/invoices/${invoiceId}/email`, data),
  remove: (invoiceId) => api.delete(`/invoices/${invoiceId}`),
  pdfURL: (invoiceId) => `${API}/invoices/${invoiceId}/pdf`,
  downloadPDF: (invoiceId) =>
    api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' }),
};

export const familyAccessAPI = {
  getSettings: (clientId) =>
    api.get(`/family/clients/${clientId}/settings`),
  setConsent: (clientId, consent) =>
    api.post(`/family/clients/${clientId}/consent`, { consent }),
  toggleNarrative: (clientId, enabled) =>
    api.post(`/family/clients/${clientId}/narrative-toggle`, { enabled }),
  regenerateVisit: (visitId) =>
    api.post(`/family/visits/${visitId}/narrative/regenerate`),
  listInvites: (clientId) =>
    api.get('/family/invites', { params: { client_id: clientId } }),
  invite: (data) => api.post('/family/invite', data),
  revoke: (familyId) => api.post(`/family/invites/${familyId}/revoke`),
  getAudit: (clientId, params = {}) =>
    api.get(`/family/clients/${clientId}/audit`, { params }),
  auditCsvURL: (clientId, params = {}) => {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''),
    ).toString();
    return `${API}/family/clients/${clientId}/audit.csv${qs ? `?${qs}` : ''}`;
  },
};

export const regulatorAPI = {
  // Manager-facing
  issue: (data) => api.post('/regulator/issue', data),
  list: (clientId) => api.get('/regulator/list', { params: { client_id: clientId } }),
  revoke: (accessId) => api.post(`/regulator/${accessId}/revoke`),
  // Regulator-facing (no auth)
  verify: (token, pin) => api.post('/regulator/verify', { token, pin }),
  snapshot: (sessionToken) =>
    api.get('/regulator/snapshot', { params: { session_token: sessionToken } }),
};
